import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getPublishedResponses } from '../utils/api';
import './FeedPage.css';

const POLL_INTERVAL = 10000;
const SCROLL_SPEED = 55; // px/sec

const ZIRVE_LOGO = 'https://musterideneyimizirvesi.com/images/zirve-logo.jpg';

export default function FeedPage() {
  const [responses, setResponses] = useState([]);
  const [displayList, setDisplayList] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const animFrameRef = useRef(null);
  const lastTimeRef = useRef(null);
  const posRef = useRef(0);

  const fetchResponses = useCallback(async () => {
    try {
      const res = await getPublishedResponses();
      setResponses(res.data);
    } catch (err) {
      console.error('Feed fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResponses();
    const interval = setInterval(fetchResponses, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchResponses]);

  useEffect(() => {
    if (responses.length > 0) {
      setDisplayList([...responses, ...responses, ...responses]);
      posRef.current = 0;
    }
  }, [responses]);

  useEffect(() => {
    if (displayList.length === 0 || !containerRef.current) return;
    const container = containerRef.current;

    const animate = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const delta = (timestamp - lastTimeRef.current) / 1000;
      lastTimeRef.current = timestamp;
      posRef.current += SCROLL_SPEED * delta;
      const totalHeight = container.scrollHeight / 3;
      if (posRef.current >= totalHeight) posRef.current -= totalHeight;
      container.scrollTop = posRef.current;
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      lastTimeRef.current = null;
    };
  }, [displayList]);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="feed-page feed-loading">
        <div className="feed-spinner" />
      </div>
    );
  }

  if (responses.length === 0) {
    return (
      <div className="feed-page">
        <FeedHeaderBar count={0} />
        <div className="empty-msg"><p>Henüz yayınlanmış yorum bulunmuyor.</p></div>
        <FeedFooterBar count={0} />
      </div>
    );
  }

  return (
    <div className="feed-page">
      <FeedHeaderBar count={responses.length} />

      <div className="feed-scroll-wrapper" ref={containerRef}>
        {displayList.map((r, i) => (
          <div key={`${r.id}-${i}`} className="feed-card">
            <div className="feed-card-quote">"</div>
            <p className="feed-card-text">{r.text}</p>
            <div className="feed-card-meta">
              <span className="feed-card-date">{formatDate(r.publishedAt || r.createdAt)}</span>
            </div>
          </div>
        ))}
      </div>

      <FeedFooterBar count={responses.length} />
    </div>
  );
}

function FeedHeaderBar({ count }) {
  return (
    <div className="feed-header-bar">
      <div className="feed-header-content">
        <img src={ZIRVE_LOGO} alt="Müşteri Deneyimi Zirvesi" className="feed-zirve-logo" />
        <div className="feed-right">
          <div className="feed-live-dot">
            <span className="live-pulse" />
            <span>Canlı</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeedFooterBar({ count }) {
  return (
    <div className="feed-footer-bar">
      <span className="feed-count">{count} katılımcı görüşünü paylaştı</span>
      <div className="feed-brand">
        <span>
          Powered by{' '}
          <a
            href="https://cloud4feed.com"
            target="_blank"
            rel="noopener noreferrer"
            className="c4f-link"
          >
            Cloud4Feed
          </a>
        </span>
      </div>
    </div>
  );
}
